import type { ApiErrorBody, ErrorCode, StreamEvent } from '@tarot/contracts';
import { platform } from '@/platform';

const BASE = `${import.meta.env.VITE_API_URL ?? ''}/api`;
const TOKEN_KEY = 'tarot.token';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly details: unknown;
  constructor(readonly status: number, body: Partial<ApiErrorBody>) {
    super(body.message ?? `HTTP ${status}`);
    this.code = body.code ?? 'INTERNAL';
    this.details = body.details;
  }
}

let token: string | null = localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string | null) => { token = t; if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };
export const getToken = () => token;

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Partial<ApiErrorBody> & T;
  if (!res.ok) {
    if (res.status === 401) setToken(null);
    throw new ApiError(res.status, json);
  }
  return json;
}

export const http = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b ?? {}),
  put: <T>(p: string, b?: unknown) => request<T>('PUT', p, b ?? {}),
  del: <T>(p: string) => request<T>('DELETE', p),
};

/** Вход: существующий токен → /me; иначе — по данным платформы. */
export async function login(): Promise<{ token: string; startParam: string | null }> {
  const p = platform.getAuthPayload();
  const res =
    p.provider === 'telegram'
      ? await http.post<{ token: string; startParam: string | null }>('/auth/telegram', { initData: p.initData, tzOffset: p.tzOffset })
      : await http.post<{ token: string; startParam: string | null }>('/auth/dev', { devId: p.devId, tzOffset: p.tzOffset });
  setToken(res.token);
  return res;
}

/** SSE-поток трактовки. Резолвится финальным текстом; отклоняется {code, message}. */
export function streamSse(path: string, onToken: (t: string) => void, signal?: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const es = new EventSource(`${BASE}${path}?token=${encodeURIComponent(token ?? '')}`);
    const close = () => es.close();
    signal?.addEventListener('abort', () => { close(); reject({ code: 'ABORTED', message: 'Отменено' }); });
    es.addEventListener('token', (e) => onToken((JSON.parse((e as MessageEvent).data) as Extract<StreamEvent, { event: 'token' }>['data']).t));
    es.addEventListener('done', (e) => { close(); resolve((JSON.parse((e as MessageEvent).data) as { text: string }).text); });
    es.addEventListener('off_topic', (e) => { close(); reject({ code: 'OFF_TOPIC', ...(JSON.parse((e as MessageEvent).data) as { message: string }) }); });
    es.addEventListener('error', (e) => {
      close();
      let d: { code?: string; message?: string } = {};
      try { d = JSON.parse((e as MessageEvent).data ?? '{}'); } catch { /* сетевая ошибка без данных */ }
      reject({ code: d.code ?? 'NETWORK', message: d.message ?? 'Соединение прервано' });
    });
  });
}
